import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import GlobalLoadingIndicator from '../global-loading-indicator'
import RepresentMoney from '../../utility/represent-money'
import * as API from '../../utility/api'
import download from '../../lib/downloader'

const TOP_EVENTS_LIMIT = 5

const formatMoneyPlain = sum => {
  if (sum == null || sum === '') {
    return '—'
  }

  return `${(parseInt(sum, 10) / 100).toFixed(2).replace('.', ',')} €`
}

const formatCount = value => {
  const number = Number(value) || 0
  return number.toLocaleString('fi-FI')
}

const formatDelta = pct => {
  if (pct == null || Number.isNaN(Number(pct))) {
    return null
  }

  const value = Number(pct)
  const abs = Math.abs(value).toLocaleString('fi-FI', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })
  if (value > 0) {
    return `+${abs} %`
  }
  if (value < 0) {
    return `−${abs} %`
  }
  return `${abs} %`
}

const currentSeasonRange = (now = moment()) => {
  const startYear = now.month() >= 9 ? now.year() : now.year() - 1
  const start = moment({ year: startYear, month: 9, date: 1 })
  const end = moment({ year: startYear + 1, month: 8, date: 30 })
  return { start, end }
}

const pricingBadge = row => {
  if (row.pricing_model === 'LEGACY_RESALE') {
    return <span className="badge badge--info">Vanha</span>
  }

  if (row.pricing_model) {
    return <span className="badge badge--warning">Uusi</span>
  }

  return <span className="badge badge--quiet">—</span>
}

const csvEscape = value => {
  let text = value == null ? '' : String(value)
  if (/^[=+\-@|]/.test(text)) {
    text = `'${text}`
  }
  if (/[;"\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

const Kpi = ({ label, children, delta }) => {
  const formatted = formatDelta(delta)
  const deltaClass =
    delta > 0 ? 'is-up' : delta < 0 ? 'is-down' : undefined

  return (
    <div className="admin-v2__kpi">
      <p className="admin-v2__kpi-label">{label}</p>
      <div className="admin-v2__kpi-row">
        <span className="admin-v2__kpi-value">{children}</span>
        {formatted && (
          <span className={['admin-v2__kpi-delta', deltaClass].filter(Boolean).join(' ')}>
            {formatted}
          </span>
        )}
      </div>
    </div>
  )
}

const Stats = () => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const fallbackSeason = useMemo(() => currentSeasonRange(), [])

  useEffect(() => {
    let cancelled = false

    API.GET('admin/stats')
      .then(({ data }) => {
        if (cancelled) {
          return
        }
        setStats(data)
        setLoadError(null)
        setLoading(false)
      })
      .catch(error => {
        if (cancelled) {
          return
        }
        if (API.isHandledAuthError(error)) {
          setLoading(false)
          return
        }
        setLoadError('Tietojen lataus epäonnistui.')
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const season = stats && stats.season
  const changePct = (season && season.change_pct) || {}
  const seasonSales =
    (season && season.current && season.current.total_sales) ||
    (stats && stats.total_sales)
  const topEvents = ((season && season.top_events) || []).slice(
    0,
    TOP_EVENTS_LIMIT
  )
  const overpaid = (stats && stats.overpaid) || { count: 0, amount: 0 }
  const topTotals = topEvents.reduce(
    (acc, row) => {
      acc.count += Number(row.tickets_sold) || 0
      acc.amount += Number(row.total_sold_amount) || 0
      return acc
    },
    { count: 0, amount: 0 }
  )
  const seasonStart = season
    ? moment(season.start, 'YYYY-MM-DD')
    : fallbackSeason.start
  const seasonEnd = season
    ? moment(season.end, 'YYYY-MM-DD')
    : fallbackSeason.end
  const updatedAt =
    stats && stats.generated_at ? moment(stats.generated_at) : null

  const downloadCsv = () => {
    const lines = [
      ['Mittari', 'Arvo', 'Muutos vs. edellinen kausi'].map(csvEscape).join(';'),
      [
        'Lippuja myynnissä',
        stats ? stats.ticket_counts.for_sale : '',
        formatDelta(changePct.for_sale) || ''
      ]
        .map(csvEscape)
        .join(';'),
      [
        'Myynti yhteensä',
        seasonSales ? formatMoneyPlain(seasonSales.total_amount) : '',
        formatDelta(changePct.total_amount) || ''
      ]
        .map(csvEscape)
        .join(';'),
      [
        'Lippuja myyty',
        seasonSales ? seasonSales.sold_tickets_count : '',
        formatDelta(changePct.sold_tickets_count) || ''
      ]
        .map(csvEscape)
        .join(';'),
      [
        'Keskimääräinen maksu',
        seasonSales ? formatMoneyPlain(seasonSales.average_payment_amount) : '',
        formatDelta(changePct.average_payment_amount) || ''
      ]
        .map(csvEscape)
        .join(';'),
      '',
      ['Tapahtuma', 'Järjestäjä', 'Hinnoittelumalli', 'Myynti kpl', 'Myynti yht.']
        .map(csvEscape)
        .join(';')
    ]

    topEvents.forEach(row => {
      lines.push(
        [
          row.event,
          row.organizer,
          row.pricing_model_label || row.pricing_model || '',
          row.tickets_sold,
          formatMoneyPlain(row.total_sold_amount)
        ]
          .map(csvEscape)
          .join(';')
      )
    })

    const csv = `\uFEFF${lines.join('\n')}`
    download(
      `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`,
      `liputon-statistiikka-${moment().format('YYYY-MM-DD')}.csv`
    )
  }

  return (
    <React.Fragment>
      <div className="admin-v2__pagehead">
        <div>
          <h1>Statistiikka</h1>
          <p className="admin-v2__subtitle">
            Koko palvelun myynti ja lipputilanne. Muutos vs. edellinen kausi.
            {updatedAt && (
              <>
                {' '}
                Päivitetty {updatedAt.format('D.M.YYYY')} klo{' '}
                {updatedAt.format('HH:mm')}.
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={downloadCsv}
          disabled={!stats}
        >
          Lataa CSV
        </button>
      </div>

      {loading && (
        <div>
          <GlobalLoadingIndicator center />
        </div>
      )}

      {loadError && (
        <p className="field__error" role="alert">
          {loadError}
        </p>
      )}

      {stats && !loading && (
        <div className="admin-v2__kpis">
          <Kpi label="Lippuja myynnissä" delta={changePct.for_sale}>
            {formatCount(stats.ticket_counts.for_sale)}
          </Kpi>
          <Kpi label="Myynti yhteensä" delta={changePct.total_amount}>
            <RepresentMoney
              sum={seasonSales && seasonSales.total_amount}
              eurSuffix
            />
          </Kpi>
          <Kpi label="Lippuja myyty" delta={changePct.sold_tickets_count}>
            {formatCount(seasonSales && seasonSales.sold_tickets_count)}
          </Kpi>
          <Kpi
            label="Keskimääräinen maksu"
            delta={changePct.average_payment_amount}
          >
            <RepresentMoney
              sum={seasonSales && seasonSales.average_payment_amount}
              eurSuffix
            />
          </Kpi>
        </div>
      )}

      <section className="admin-v2__panel">
        <div className="admin-v2__panel-head">
          <div>
            <h2>Myydyimmät tapahtumat</h2>
            <p className="admin-v2__panel-meta">
              Kausi {seasonStart.format('D.M.YYYY')}–
              {seasonEnd.format('D.M.YYYY')} · järjestäjätason erittely
              saatavilla Raportit-näkymässä
            </p>
          </div>
          <Link className="btn btn--ghost" to="/admin/raportit">
            Näytä kaikki
          </Link>
        </div>
        <div className="admin-v2__panel-body">
          {!loading && !loadError && topEvents.length === 0 && (
            <p className="table-empty">Ei myyntiä valitulla kaudella.</p>
          )}
          {!loading && !loadError && topEvents.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tapahtuma</th>
                    <th>Järjestäjä</th>
                    <th>Hinnoittelumalli</th>
                    <th className="numeric">Myynti kpl</th>
                    <th className="numeric">Myynti yht.</th>
                  </tr>
                </thead>
                <tbody>
                  {topEvents.map((row, index) => (
                    <tr
                      key={
                        row.report_row_key ||
                        `${row.event}-${row.organizer}-${row.pricing_model}-${index}`
                      }
                    >
                      <td>{row.event}</td>
                      <td className="muted">{row.organizer}</td>
                      <td>{pricingBadge(row)}</td>
                      <td className="numeric">
                        {formatCount(row.tickets_sold)}
                      </td>
                      <td className="numeric">
                        <RepresentMoney
                          sum={row.total_sold_amount}
                          eurSuffix
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="is-total">
                    <td>Yhteensä (top {topEvents.length})</td>
                    <td />
                    <td />
                    <td className="numeric">{formatCount(topTotals.count)}</td>
                    <td className="numeric">
                      <RepresentMoney sum={topTotals.amount} eurSuffix />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {overpaid.count > 0 && (
        <div className="notice notice--warning">
          <div>
            <p>
              <strong>
                {overpaid.count} lippua, joissa on liikaa maksuja
              </strong>{' '}
              — ei käsitelty, yhteensä {formatMoneyPlain(overpaid.amount)}.
            </p>
          </div>
          <Link className="btn btn--ghost" to="/admin/tyokalut">
            Käsittele
          </Link>
        </div>
      )}
    </React.Fragment>
  )
}

export default Stats
